import { Grid, Button, Menu, MenuItem, Tooltip } from '@vapor/material'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FelState, FiscalType, usePaidInFullDocumentsLazyQuery } from 'src/api/api.generated'
import { CoQuery } from './co-query-wrapper'
import { DocumentType } from 'src/api/api.generated'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPrint } from '@fortawesome/free-solid-svg-icons'
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { useHotelDate } from 'src/app/hooks/context/context.hook'
import { compareDates } from 'src/utils/date'

export type LayoutToolbarProps = {
  queryData: CoQuery
  tenantId: string
  propertyId: string
  creditNoteExists: boolean
  documentType?: DocumentType
  onClose: () => void
  handleSubmit: (draft: boolean, finalized: boolean, updatePaymensDetailsOnly: boolean) => (e?: React.BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>
  handleDelete: () => void
  handleUndo: () => void
  handleCreateCreditNote: (manualDocument: boolean) => void
  onPrint: () => void
  loading?: boolean
  tsPayPaymentActiveIds?: string[]
  handleSendPaidInFullEmail: () => void
  handleSendPaidInFullMessage: () => void
  documentEmailsLog: boolean
  handleViewDocumentEmailsLog: () => void
  allowSaveOnFinalizedDocument: boolean
  onDocumentSendByEmail: () => void
  allowSavePaymentDetails: boolean
  // handleSaveOnFinalizedDocument: () => void
}

export function LayoutToolbar({
  tenantId,
  propertyId,
  queryData,
  creditNoteExists,
  documentType,
  onClose,
  handleSubmit,
  handleDelete,
  onPrint,
  loading,
  handleUndo,
  handleCreateCreditNote,
  tsPayPaymentActiveIds,
  handleSendPaidInFullEmail,
  handleSendPaidInFullMessage,
  documentEmailsLog,
  handleViewDocumentEmailsLog,
  allowSaveOnFinalizedDocument,
  onDocumentSendByEmail,
  allowSavePaymentDetails,
}: // handleSaveOnFinalizedDocument,
LayoutToolbarProps) {
  const { t } = useTranslation()

  const submitDraft = useMemo(() => handleSubmit(true, false, false), [handleSubmit])
  const submitFinal = useMemo(() => handleSubmit(false, false, false), [handleSubmit])
  const submitOnFinalized = useMemo(() => handleSubmit(false, true, false), [handleSubmit])
  const submitPaymentDetails = useMemo(() => handleSubmit(false, false, true), [handleSubmit])
  const [getPaidInFullDocuments] = usePaidInFullDocumentsLazyQuery()

  const document = useMemo(() => {
    if (queryData.mode !== 'READ_ONLY') return undefined
    return queryData.document
  }, [queryData])

  const isPaidInFullDraftWithTsPay = useMemo(() => {
    if (queryData.mode === 'UPDATE' && queryData.document.fiscalType === 'PAID_IN_FULL') {
      // Controllo se ci sono pagamenti con gateway
      if (queryData.document.documentPaymentDetails.some((p) => tsPayPaymentActiveIds?.includes(p.paymentActiveId))) return true
    }
    return false
  }, [queryData, tsPayPaymentActiveIds])

  const disableSendWithWhatsapp = useMemo(() => {
    if (queryData.mode === 'UPDATE') {
      const document = queryData.document
      if (document) {
        const phoneNumber = document.customer.phoneContacts?.filter((n) => n.isDefault)?.[0]
        if (phoneNumber) return false
      }
    }
    return true
  }, [queryData])

  const showSave = useMemo(() => {
    if (queryData.mode === 'CREATE') return true
    if (queryData.mode === 'UPDATE') {
      if (isPaidInFullDraftWithTsPay) return false
      return true
    }
    return false
  }, [queryData, isPaidInFullDraftWithTsPay])
  const showDelete = useMemo(() => queryData.mode === 'UPDATE', [queryData])

  const notaDiCreditoButtonText = useMemo(() => {
    if (queryData.mode !== 'READ_ONLY') return false
    const document = queryData.document
    return document.fiscalType === 'RECEIPT' || document.fiscalType === 'NON_FISCAL' ? t('pms.common.checkout_document.credit_note_reso') : t('pms.common.checkout_document.credit_note')
  }, [queryData])

  const [showUndo, setShowUndo] = useState<boolean>(false)

  useEffect(() => {
    const fetchData = async () => {
      if (queryData.mode !== 'READ_ONLY') {
        setShowUndo(false)
        return
      }
      const document = queryData.document

      if (document.documentType === 'ADVANCE' && !document.cashDesk.open) {
        setShowUndo(false)
        return
      }

      const haveUnpaidPayments = document.documentPaymentDetails.some((p) => p.paymentActive.paymentType.code === 'UNPAID')
      const havePaidInFullDocuments = async () => {
        try {
          const { data } = await getPaidInFullDocuments({
            variables: {
              filter: {
                tenantId: { eq: tenantId },
                propertyId: { eq: propertyId },
                referenceDocumentId: {
                  eq: document.id,
                },
                fiscalType: { eq: 'PAID_IN_FULL' },
                documentType: 'PAID_IN_FULL',
              },
            },
          })
          if (data?.documents?.length) return true
          return false
        } catch (error) {
          return false
        }
      }

      if ((document?.depositAssigneds ?? []).filter((da) => da.document).length > 0) {
        setShowUndo(false)
        return
      }

      if (haveUnpaidPayments && (await havePaidInFullDocuments())) {
        setShowUndo(false)
        return
      }

      if (document.fiscalType === 'PAID_IN_FULL') {
        setShowUndo(true)
        return
      }

      if (document.fiscalType === 'RECEIPT') {
        if (document.creditNote?.id || document.referenceDocument?.id || document.scelAdditionalInfo?.sent) setShowUndo(false)
        else setShowUndo(true)
        return
      }
      if (document.fiscalType === 'INVOICE') {
        if (!document.felAdditionalInfo) {
          setShowUndo(true)
          return
        }
        if (!document.felAdditionalInfo.sent) {
          setShowUndo(true)
          return
        }
        if (document.felAdditionalInfo.sent && document.felAdditionalInfo.state === FelState.SCARTATO) {
          setShowUndo(true)
          return
        }
        return
      }

      const advanceAmount =
        document.depositAssigneds
          ?.filter((da) => da.document)
          ?.map((da) => da.amount)
          .reduce((prev, curr) => prev + curr, 0) ?? 0

      if (document.fiscalType === 'NON_FISCAL' && advanceAmount === 0) {
        setShowUndo(true)
        return
      }

      setShowUndo(true)
      return
    }
    fetchData()
  }, [queryData, getPaidInFullDocuments])

  const showCreditNote = useMemo(() => {
    if (!document) return false
    const advanceAmount =
      document.depositAssigneds
        ?.filter((da) => da.document)
        ?.map((da) => da.amount)
        .reduce((prev, curr) => prev + curr, 0) ?? 0

    const residue = document.totalAmount - advanceAmount
    if (document.documentType === 'ADVANCE' && residue <= 0) return false

    if ((document.fiscalType === 'RECEIPT' || document.fiscalType === 'NON_FISCAL') && residue > 0) return true

    if (document.fiscalType === 'INVOICE') {
      const fel = document.felAdditionalInfo
      if (!fel || !fel.sent || !fel.state) return false

      const totalDocumentUnpaid = document.documentPaymentDetails.reduce((total, d) => total + (d.paymentActive.paymentType.code === 'UNPAID' ? d.amount : 0), 0)
      if (totalDocumentUnpaid < document.unpaidAmount) {
        return false //Se ci sono sospesi anche solo parzialmente quietanzati il documento non può essere riportato in provvisorio.
      }

      const states: FelState[] = [FelState.NON_RECAPITATO, FelState.ACCETTATO, FelState.RIFIUTATO, FelState.NESSUNA_RISPOSTA, FelState.INVIATO] // Stati nota di credito
      return states.includes(fel.state)
    }

    return false
  }, [queryData])

  const showPrint = useMemo(() => queryData.mode === 'READ_ONLY' || queryData.mode === 'UPDATE', [queryData.mode])
  const showSendDocumentByEmail = useMemo(() => queryData.mode === 'READ_ONLY', [queryData.mode])

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const menuOpen = useMemo(() => anchorEl != null, [anchorEl])
  return (
    <Grid container spacing={2} direction="row-reverse">
      {allowSaveOnFinalizedDocument && (
        <Grid item>
          <Button variant="outlined" onClick={submitOnFinalized} disabled={loading}>
            {t('commons.save')}
          </Button>
        </Grid>
      )}
      {allowSavePaymentDetails && (
        <Grid item>
          <Button variant="outlined" onClick={submitPaymentDetails} disabled={loading}>
            {t('commons.save')}
          </Button>
        </Grid>
      )}
      {showSave && (
        <Grid item>
          <Button variant="contained" onClick={submitFinal} disabled={loading}>
            {t('pms.common.checkout_document.save')}
          </Button>
        </Grid>
      )}
      {showSave && (
        <Grid item>
          <Button variant="contained" onClick={submitDraft} disabled={loading}>
            {t('pms.common.checkout_document.save_draft')}
          </Button>
        </Grid>
      )}
      {showDelete && (
        <Grid item>
          <Button variant="outlined" color="error" onClick={handleDelete} disabled={loading}>
            {t('commons.delete')}
          </Button>
        </Grid>
      )}
      {showUndo && (
        <Grid item>
          <Button variant="contained" onClick={handleUndo} disabled={loading}>
            {t('pms.common.checkout_document.undo_document')}
          </Button>
        </Grid>
      )}
      {showCreditNote && (
        <Grid item>
          <Button
            variant="contained"
            onClick={(event) => {
              if (documentType !== DocumentType.ADVANCE && document?.fiscalType === FiscalType.INVOICE) {
                // You have the choice if NOT ADVANCE and is INVOICE
                setAnchorEl(event.currentTarget)
              } else {
                handleCreateCreditNote(false)
              }
            }}
            disabled={loading || creditNoteExists}
          >
            {notaDiCreditoButtonText}
          </Button>
          <Menu open={menuOpen} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
            <MenuItem //
              onClick={() => {
                handleCreateCreditNote(true)
                setAnchorEl(null)
              }}
            >
              {t('pms.common.checkout_document.create_credit_note_manual_document')}
            </MenuItem>
            <MenuItem //
              onClick={() => {
                handleCreateCreditNote(false)
                setAnchorEl(null)
              }}
            >
              {notaDiCreditoButtonText}
            </MenuItem>
          </Menu>
        </Grid>
      )}
      {isPaidInFullDraftWithTsPay && (
        <Grid item>
          <Button variant="outlined" onClick={handleSendPaidInFullEmail} startIcon={<FontAwesomeIcon icon={faEnvelope} />} disabled={loading}>
            {t('pms.commons.document.send_payment_link_email')}
          </Button>
        </Grid>
      )}
      {isPaidInFullDraftWithTsPay && (
        <Grid item>
          <Tooltip title={disableSendWithWhatsapp ? 'Per poter inviare il link tramite whatsapp è necessario che nella scheda del cliente sia presente il recapito telefonico' : ''}>
            <div>
              <Button variant="outlined" onClick={handleSendPaidInFullMessage} startIcon={<FontAwesomeIcon icon={faWhatsapp} />} disabled={loading || disableSendWithWhatsapp}>
                {t('pms.commons.document.send_payment_link_whatsapp')}
              </Button>
            </div>
          </Tooltip>
        </Grid>
      )}
      <Grid item>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          {t('commons.close')}
        </Button>
      </Grid>
      {showPrint && (
        <Grid item>
          <Button variant="outlined" onClick={onPrint} disabled={loading} startIcon={<FontAwesomeIcon icon={faPrint} />}>
            {t('commons.print')}
          </Button>
        </Grid>
      )}
      {showSendDocumentByEmail && (
        <Grid item>
          <Button variant="outlined" onClick={onDocumentSendByEmail} disabled={loading}>
            {t('pms.documents.send_by_email')}
          </Button>
        </Grid>
      )}
      {documentEmailsLog && (
        <Grid item>
          <Button variant="text" onClick={handleViewDocumentEmailsLog}>
            {t('pms.documents.email_log_title')}
          </Button>
        </Grid>
      )}
    </Grid>
  )
}
